import { Actor } from "../actor.js";
import { Layer } from "../tile.js";
import { hasOwnProperty, relativeToAbsolute } from "../helpers.js";
import { actorDB, Decision, getTagFlag, hasTagOverlap } from "../const.js";
import { Fire } from "./terrain.js";
import { iterableFind } from "../iterableHelpers.js";
import { onLevelAfterTick } from "../level.js";
import { GlitchInfo } from "../parsers/nccs.pb.js";
export class Monster extends Actor {
    blocks() {
        return true;
    }
    getLayer() {
        return Layer.MOVABLE;
    }
    bumped(other, _bumpedDirection) {
        // Monsters kill players which bump into
        if (other.hasTag("real-playable") &&
            !this.hasTag("ignore-default-monster-kill") &&
            !other.hasTag("ignore-default-monster-kill"))
            other.destroy(this);
    }
    // Monsters kill players which they bump into
    bumpedActor(other, _bumpedDirection, _exiting) {
        // Monsters kill players which bump into them if they can move into them
        if (other.hasTag("real-playable") &&
            !this.hasTag("ignore-default-monster-kill") &&
            !other.hasTag("ignore-default-monster-kill"))
            other.destroy(this);
    }
}
Monster.tags = ["autonomous-monster", "normal-monster", "movable"];
export class Centipede extends Monster {
    constructor() {
        super(...arguments);
        this.id = "centipede";
        this.transmogrifierTarget = "fireball";
    }
    decideMovement() {
        const dir = relativeToAbsolute(this.direction);
        return [dir.RIGHT, dir.FORWARD, dir.LEFT, dir.BACKWARD];
    }
}
actorDB["centipede"] = Centipede;
export class Ant extends Monster {
    constructor() {
        super(...arguments);
        this.id = "ant";
        this.transmogrifierTarget = "glider";
    }
    decideMovement() {
        const dir = relativeToAbsolute(this.direction);
        return [dir.LEFT, dir.FORWARD, dir.RIGHT, dir.BACKWARD];
    }
}
//
Ant.blockedByTags = ["canopy"];
actorDB["ant"] = Ant;
export class Glider extends Monster {
    constructor() {
        super(...arguments);
        this.id = "glider";
        this.transmogrifierTarget = "centipede";
    }
    decideMovement() {
        const dir = relativeToAbsolute(this.direction);
        return [dir.FORWARD, dir.LEFT, dir.RIGHT, dir.BACKWARD];
    }
}
Glider.ignoreTags = ["water-ish"];
actorDB["glider"] = Glider;
export class Fireball extends Monster {
    constructor() {
        super(...arguments);
        this.id = "fireball";
        this.transmogrifierTarget = "ant";
    }
    decideMovement() {
        const dir = relativeToAbsolute(this.direction);
        return [dir.FORWARD, dir.RIGHT, dir.LEFT, dir.BACKWARD];
    }
}
Fireball.collisionIgnoreTags = ["fire"];
Fireball.ignoreTags = ["fire"];
Fireball.tags = ["autonomous-monster", "normal-monster", "movable", "melting"];
actorDB["fireball"] = Fireball;
export class Ball extends Monster {
    constructor() {
        super(...arguments);
        this.id = "ball";
        this.transmogrifierTarget = "walker";
    }
    decideMovement() {
        const dir = relativeToAbsolute(this.direction);
        return [dir.FORWARD, dir.BACKWARD];
    }
}
actorDB["ball"] = Ball;
function getPursuitCoords(actor, target, reverse = false) {
    // This uses the visual position of the target
    const targetPos = target.getVisualPosition();
    const dx = actor.tile.x - targetPos[0], dy = actor.tile.y - targetPos[1];
    const directions = [];
    const addAmount = reverse ? 2 : 0;
    if (dx)
        directions.push((Math.sign(dx) + 2 + addAmount) % 4);
    if (dy)
        directions.push((Math.sign(dy) + 3 - addAmount) % 4);
    if (Math.abs(dy) >= Math.abs(dx))
        directions.reverse();
    return directions;
}
export class TeethRed extends Monster {
    constructor() {
        super(...arguments);
        this.id = "teethRed";
        this.transmogrifierTarget = "teethBlue";
    }
    decideMovement() {
        if (!this.level.selectedPlayable || (this.level.currentTick + 5) % 8 >= 4)
            return [];
        return getPursuitCoords(this, this.level.selectedPlayable, this.level.selectedPlayable.hasTag("scares-teeth-red"));
    }
}
actorDB["teethRed"] = TeethRed;
export class TeethBlue extends Monster {
    constructor() {
        super(...arguments);
        this.id = "teethBlue";
        this.transmogrifierTarget = "teethRed";
    }
    decideMovement() {
        if (!this.level.selectedPlayable || (this.level.currentTick + 5) % 8 >= 4)
            return [];
        return getPursuitCoords(this, this.level.selectedPlayable, this.level.selectedPlayable.hasTag("scares-teeth-blue"));
    }
}
actorDB["teethBlue"] = TeethBlue;
export class FloorMimic extends Monster {
    constructor() {
        super(...arguments);
        this.id = "floorMimic";
    }
    decideMovement() {
        if (!this.level.selectedPlayable || (this.level.currentTick + 5) % 16 >= 4)
            return [];
        return getPursuitCoords(this, this.level.selectedPlayable);
    }
}
actorDB["floorMimic"] = FloorMimic;
export class TankBlue extends Monster {
    constructor() {
        super(...arguments);
        this.id = "tankBlue";
        this.transmogrifierTarget = "tankYellow";
        this.turnPending = this.customData === "rotating";
    }
    decideMovement() {
        if (this.turnPending) {
            this.turnPending = false;
            this.customData = "";
            return [(this.direction + 2) % 4];
        }
        return [this.direction];
    }
    rotateTank() {
        this.turnPending = !this.turnPending;
        this.customData = this.turnPending ? "rotating" : "";
    }
}
actorDB["tankBlue"] = TankBlue;
onLevelAfterTick.push(level => {
    if (level.blueButtonPressed) {
        for (const tank of level.decidingActors) {
            if (hasOwnProperty(tank, "rotateTank") &&
                typeof tank.rotateTank === "function")
                tank.rotateTank();
        }
        level.blueButtonPressed = false;
    }
});
export class BlobMonster extends Monster {
    constructor() {
        super(...arguments);
        this.id = "blob";
        this.moveSpeed = 8;
    }
    transmogrifierTarget() {
        return [
            "glider",
            "centipede",
            "fireball",
            "ant",
            "walker",
            "ball",
            "teethRed",
            "tankBlue",
            "teethBlue",
        ][this.level.random() % 9];
    }
    newTileJoined() {
        const spreadedSlime = this.oldTile &&
            iterableFind(this.oldTile.allActors, (val) => val.hasTag("slime"));
        if (spreadedSlime && !this.tile.hasLayer(spreadedSlime.layer))
            new actorDB[spreadedSlime.id](this.level, this.tile.position, spreadedSlime.customData);
    }
    decideMovement() {
        // Weird quirk: blobs don't check collision at decision time
        this.moveDecision = ((this.level.random() + this.level.blobMod()) % 4) + 1;
        return [];
    }
    blockedBy(other) {
        return (other.hasTag("canopy") &&
            !!(this.tile[Layer.SPECIAL] && this.tile[Layer.SPECIAL].hasTag("canopy")));
    }
}
BlobMonster.immuneTags = ["slime"];
actorDB["blob"] = BlobMonster;
export class Walker extends Monster {
    constructor() {
        super(...arguments);
        this.id = "walker";
        this.transmogrifierTarget = "ball";
    }
    decideMovement() {
        if (!this.checkCollision(this.direction))
            return [(this.level.random() + this.direction) % 4];
        return [this.direction];
    }
}
actorDB["walker"] = Walker;
export class LitTNT extends Monster {
    constructor() {
        super(...arguments);
        this.lifeLeft = 253;
        this.explosionStage = 0;
        this.id = "tntLit";
    }
    nukeTile(tile) {
        var _a;
        let protectedLayer = Layer.STATIONARY;
        const tileHadMovable = tile.hasLayer(Layer.MOVABLE);
        let movableDied = false;
        // TODO Canopies
        if (tileHadMovable)
            protectedLayer = Layer.STATIONARY + 1; // Protect stationary only
        const protector = iterableFind(tile.allActors, val => val.hasTag("blocks-tnt"));
        if (protector && !(tileHadMovable && tile[this.layer].id === this.id))
            protectedLayer = protector.layer;
        for (const actor of Array.from(tile.allActorsReverse))
            if (actor.layer >= protectedLayer) {
                (_a = actor.bumped) === null || _a === void 0 ? void 0 : _a.call(actor, this, Math.abs(tile.x - this.tile.x) > Math.abs(tile.y - this.tile.y)
                    ? 2 + Math.sign(tile.x - this.tile.x)
                    : 1 + Math.sign(tile.y - this.tile.y));
                if (!actor.exists)
                    continue;
                let destroyed = actor.destroy(actor === protector && protectedLayer !== actor.layer ? null : this, actor.layer === Layer.STATIONARY || actor.layer === Layer.MOVABLE
                    ? "explosion"
                    : null);
                if (destroyed && actor.layer === Layer.MOVABLE) {
                    movableDied = true;
                }
            }
        // Create a memorial fire if a movable got blown up (if we can)
        if (tileHadMovable && movableDied && !tile.hasLayer(Layer.STATIONARY))
            new Fire(this.level, tile.position);
    }
    onEachDecision() {
        if (this.lifeLeft > 0)
            this.lifeLeft--;
        else
            this.explosionStage++;
        if (!this.explosionStage)
            return;
        // For ice blocks
        this.recomputeTags(false);
        this.tags |= getTagFlag("melting");
        this.immuneTags |= getTagFlag("bowling-ball");
        for (const tile of this.tile.getDiamondSearch(this.explosionStage))
            if (Math.abs(tile.x - this.tile.x) < 3 &&
                Math.abs(tile.y - this.tile.y) < 3)
                this.nukeTile(tile);
        if (this.explosionStage === 2 || this.explosionStage === 3) {
            // Dynamite sneaking check: If there's a playable in a ring we've nuked  last subtick,
            // it means that the playable sneaked through the ring we are nuking this subtick
            // by moving closer to the dynamite
            for (const tile of this.tile.getDiamondSearch(this.explosionStage - 1)) {
                const playable = tile.findActor(actor => actor.hasTag("real-playable"));
                if (!playable || !playable.oldTile)
                    continue;
                // Also check if the playable was actually on a nuked tile last
                // subtick, "sneaking" onto a 2-tile from outside the explosion range
                // is 100% legal
                const xOff = Math.abs(playable.oldTile.x - this.tile.x);
                const yOff = Math.abs(playable.oldTile.y - this.tile.y);
                if (xOff + yOff === this.explosionStage && xOff < 3 && yOff < 3) {
                    this.level.addGlitch({
                        glitchKind: GlitchInfo.KnownGlitches.DYNAMITE_EXPLOSION_SNEAKING,
                        location: { x: tile.x, y: tile.y },
                    });
                }
            }
        }
        if (this.explosionStage >= 3)
            this.nukeTile(this.tile);
        this.recomputeTags(true);
    }
}
LitTNT.tags = ["movable", "cc1block", "tnt"];
actorDB["tntLit"] = LitTNT;
export class TankYellow extends Monster {
    constructor() {
        super(...arguments);
        this.id = "tankYellow";
        this.transmogrifierTarget = "tankBlue";
        this.movePending = this.customData === "rotating" ? -1 : Decision.NONE;
    }
    decideMovement() {
        if (this.movePending) {
            //@ts-expect-error You literally didn't check if -1 is Decision 3 lines ago, shut up
            if (this.movePending === -1) {
                this.movePending = this.direction + 1;
            }
            this.customData = "";
            if (this.checkCollision(this.movePending - 1))
                this.moveDecision = this.level.resolvedCollisionCheckDirection + 1;
            this.direction = this.level.resolvedCollisionCheckDirection;
            this.movePending = Decision.NONE;
        }
        return [];
    }
    rotateYellowTank(data) {
        this.customData = "rotating";
        this.movePending = data;
    }
}
TankYellow.tags = ["normal-monster", "movable"];
TankYellow.pushTags = ["block"];
actorDB["tankYellow"] = TankYellow;
onLevelAfterTick.push(level => {
    if (level.currentYellowButtonPress) {
        for (const tank of level.decidingActors) {
            if (hasOwnProperty(tank, "rotateYellowTank") &&
                typeof tank.rotateYellowTank === "function")
                tank.rotateYellowTank(level.currentYellowButtonPress);
        }
        level.currentYellowButtonPress = Decision.NONE;
    }
});
export class RollingBowlingBall extends Monster {
    constructor() {
        super(...arguments);
        this.id = "bowlingBallRolling";
    }
    decideMovement() {
        return [this.direction];
    }
    bumped(other, direction) {
        this.bumpedActor(other, direction, false);
    }
    bumpedActor(other, direction, exiting) {
        if (!this.exists)
            return;
        if ((!exiting && other._internalBlocks(this, direction)) ||
            (exiting && other._internalExitBlocks(this, direction))) {
            if (other.layer === Layer.MOVABLE) {
                other.destroy(this);
                this.destroy(this);
            }
            else if (!this.slidingState)
                this.destroy(this);
        }
    }
    bumpedEdge() {
        if (!this.slidingState)
            this.destroy(this);
    }
}
RollingBowlingBall.tags = [
    "can-pickup-items",
    "movable",
    "interacts-with-closed-clone-machine",
    "bowling-ball",
];
actorDB["bowlingBallRolling"] = RollingBowlingBall;
export const roverMimicOrder = [
    "teethRed",
    "glider",
    "ant",
    "ball",
    "teethBlue",
    "fireball",
    "centipede",
    "walker",
];
export class Rover extends Monster {
    constructor() {
        super(...arguments);
        this.id = "rover";
        this.moveSpeed = 8;
        this.emulatedMonster = roverMimicOrder[0];
        this.decisionsUntilNext = 32;
    }
    decideMovement() {
        var _a;
        this.decisionsUntilNext--;
        if (!this.decisionsUntilNext) {
            this.emulatedMonster =
                roverMimicOrder[(roverMimicOrder.indexOf(this.emulatedMonster) + 1) %
                    roverMimicOrder.length];
            this.decisionsUntilNext = 32;
        }
        return (((_a = actorDB[this.emulatedMonster].prototype.decideMovement) === null || _a === void 0 ? void 0 : _a.apply(this)) || []);
    }
}
Rover.tags = ["autonomous-monster", "can-pickup-items", "movable"];
Rover.pushTags = ["block"];
Rover.blockedByTags = ["canopy"];
actorDB["rover"] = Rover;
export class MirrorChip extends Monster {
    constructor() {
        super(...arguments);
        this.id = "mirrorChip";
        this.fakes = "chip";
        this.transmogrifierTarget = "mirrorMelinda";
    }
    decideMovement() {
        if (!this.level.selectedPlayable ||
            this.level.selectedPlayable.id !== this.fakes ||
            this.level.selectedPlayable.lastDecision === Decision.NONE)
            return [];
        this.moveDecision = this.level.selectedPlayable.lastDecision;
        return [];
    }
}
MirrorChip.tags = [
    "can-pickup-items",
    "playable",
    "chip",
    "movable",
    "can-reuse-key-green",
    "plays-block-push-sfx",
];
MirrorChip.pushTags = ["block"];
actorDB["mirrorChip"] = MirrorChip;
export class MirrorMelinda extends Monster {
    constructor() {
        super(...arguments);
        this.id = "mirrorMelinda";
        this.fakes = "melinda";
        this.transmogrifierTarget = "mirrorChip";
    }
    decideMovement() {
        if (!this.level.selectedPlayable ||
            this.level.selectedPlayable.id !== this.fakes ||
            this.level.selectedPlayable.lastDecision === Decision.NONE)
            return [];
        this.moveDecision = this.level.selectedPlayable.lastDecision;
        return [];
    }
}
MirrorMelinda.tags = [
    "can-pickup-items",
    "playable",
    "melinda",
    "movable",
    "can-reuse-key-yellow",
    "plays-block-push-sfx",
];
MirrorMelinda.pushTags = ["block"];
MirrorMelinda.ignoreTags = ["ice"];
actorDB["mirrorMelinda"] = MirrorMelinda;
export class Ghost extends Monster {
    constructor() {
        super(...arguments);
        this.id = "ghost";
    }
    decideMovement() {
        const dir = relativeToAbsolute(this.direction);
        return [dir.FORWARD, dir.LEFT, dir.RIGHT, dir.BACKWARD];
    }
    blockedBy(other) {
        if (other.tile.hasLayer(Layer.ITEM_SUFFIX))
            return false;
        return hasTagOverlap(other.tags, this.ghostBlockedByTags);
    }
    collisionIgnores(other) {
        if (other.tile.hasLayer(Layer.ITEM_SUFFIX))
            return false;
        return (hasTagOverlap(other.tags, this.ghostCollisionIgnoreTags) ||
            (!hasTagOverlap(other.tags, this.nonIgnoredTags | this.ghostBlockedByTags) &&
                other.layer !== Layer.MOVABLE));
    }
    ignores(other) {
        return (!hasTagOverlap(other.tags, this.nonIgnoredTags) &&
            other.layer !== Layer.ITEM &&
            other.layer !== Layer.MOVABLE);
    }
}
Ghost.tags = [
    "can-pickup-items",
    "movable",
    "ghost",
    "weirdly-ignores-ice",
    "ignores-exit-block",
    "double-item-remove",
];
Ghost.extraTagProperties = [
    "ghostBlockedByTags",
    "nonIgnoredTags",
    "ghostCollisionIgnoreTags",
];
Ghost.ghostBlockedByTags = ["blocks-ghost", "water-ish"];
Ghost.nonIgnoredTags = [
    "machinery",
    "button",
    "door",
    "echip-gate",
    "jet",
    "no-sign",
    "ice",
    "water-ish",
];
Ghost.ignoreTags = ["bonusFlag", "bomb"];
Ghost.ghostCollisionIgnoreTags = ["door", "echip-gate", "ice"];
actorDB["ghost"] = Ghost;
//# sourceMappingURL=monsters.js.map